import React, { useEffect, useState } from "react";
import {
  useMsal,
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from "@azure/msal-react";
import { useDispatch, useSelector } from "react-redux";
import AppRoutes from "./Routes";
import SettingsRoutes from "./SettingsRoutes";
import AppLoader from "./components/AppLoader";
import "semantic-ui-css/semantic.min.css";
import "./styles/styles.css";
import "./styles/MobileResponsive.css";
import { loginRequest } from "./authConfig";
import { jwtDecode } from "jwt-decode";
import {
  loginUser,
  refreshToken,
} from "./actions/authActions";
import { fetchOrganizationDetails } from "./actions/adminConsoleAction";
import { fetchInnferreToken, fetchExternalDataset } from "./actions/dataConsoleAction";
import { wakeUpServices } from "./apis";


const App = () => {
  const { instance, accounts, inProgress } = useMsal();
  const activeAccount = instance.getActiveAccount();
  const [activeToken, setActiveToken] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [authProcessComplete, setAuthProcessComplete] = useState(false);
  const dispatch = useDispatch();
  const isLocalStorage = useSelector((state) => state.auth.isLocalStorage);
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const currentUserType = useSelector((state) => state.auth.user);

  // Hide minibot for external users
  useEffect(() => {
    const isExternalUser = localStorage.getItem("isExternalUser") === "true";

    if (isExternalUser) {
      // Add class to body for CSS-based hiding
      document.body.classList.add('external-user');

      // Override showBot function to prevent it from running
      if (window.showBot) {
        window.showBot = () => {
          console.log('[ExternalUser] showBot() blocked for external users');
        };
      }

      // Function to hide minibot elements
      const hideMinibot = () => {
        // Hide chatbot-embed if exists
        const minibotWidget = document.querySelector('#chatbot-embed');
        if (minibotWidget) {
          minibotWidget.style.display = 'none';
        }

        // Hide any iframe containing minibot URL
        const minibotIframes = document.querySelectorAll('iframe[src*="innferremini"], iframe[src*="datasetId"]');
        minibotIframes.forEach(iframe => {
          iframe.style.display = 'none';
          if (iframe.parentElement) {
            iframe.parentElement.style.display = 'none';
          }
        });
      };

      // Try immediately
      hideMinibot();

      // Set up MutationObserver to watch for minibot being added
      const observer = new MutationObserver(() => {
        hideMinibot();
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true
      });

      return () => {
        document.body.classList.remove('external-user');
        observer.disconnect();
      };
    }
  }, []);

  useEffect(() => {


    if (isLocalStorage) {
      setActiveToken(true);
      dispatch(fetchOrganizationDetails());
      // Wake up backend services after successful login
      wakeUpServices().catch(err => console.warn("Wake-up services failed:", err));
      setAuthProcessComplete(true);
      const redirectPath = sessionStorage.getItem('redirectPath');
      if (redirectPath) {

        sessionStorage.removeItem('redirectPath');
        window.location.href = redirectPath;
      }
    }
  }, [isLocalStorage, dispatch]);

  useEffect(() => {

    const interval = setInterval(() => {

      dispatch(refreshToken());
    }, 600000);

    return () => clearInterval(interval);
  }, [dispatch]);

  useEffect(() => {
    // Wake up backend services every 30 seconds
    const wakeUpInterval = setInterval(() => {
      wakeUpServices().catch(err => console.warn("Wake-up services failed:", err));
    }, 30000);

    return () => clearInterval(wakeUpInterval);
  }, []);

  useEffect(() => {
    const handleRedirect = async () => {
      const currentPath = window.location.pathname;
      const urlParams = new URLSearchParams(window.location.search);
      const redirectPath = urlParams.get('redirect');



      if (redirectPath) {
        sessionStorage.setItem('redirectPath', redirectPath);
      }

      // --- External User Bypass ---
      try {
        const isExternalUserFlag = localStorage.getItem("isExternalUser");

        if (isExternalUserFlag === "true") {
          console.log("[APP] Bypassing MSAL because isExternalUser is true");
          setIsInitializing(false);
          setAuthProcessComplete(true);
          return;
        }
      } catch (e) {

      }
      // ----------------------------

      if ((!accounts || accounts.length === 0) && inProgress === "none") {

        try {
          await instance.handleRedirectPromise();
          if (
            !instance.getActiveAccount() &&
            currentPath !== "/settings" &&
            currentPath !== "/settings/privacy-policy" &&
            currentPath !== "/settings/faq"
          ) {

            await instance.loginRedirect(loginRequest);
          }
        } catch (error) {

        } finally {
          setTimeout(() => {

            setIsInitializing(false);
            setAuthProcessComplete(true);
          }, 1500);
        }
      } else {

        if (accounts && accounts.length > 0) {
          if (accounts[0]?.idTokenClaims) {

            dispatch(loginUser(accounts[0].idTokenClaims));
            // Wake up backend services after successful login
            wakeUpServices().catch(err => console.warn("Wake-up services failed:", err));
          } else {
            const token = localStorage.getItem("token");
            if (token) {

              const code = jwtDecode(token);
              dispatch(loginUser(code));
              // Wake up backend services after successful login
              wakeUpServices().catch(err => console.warn("Wake-up services failed:", err));
            }
          }
        }
        setTimeout(() => {

          setIsInitializing(false);
          setAuthProcessComplete(true);
        }, 1500);
      }
    };

    handleRedirect();
  }, [accounts, inProgress, instance, dispatch]);


  const isPublicRoute = (path) => {
    const result =
      path === "/settings" ||
      path === "/settings/privacy-policy" ||
      path === "/settings/faq";

    return result;
  };

  if (isInitializing || !authProcessComplete || inProgress === "startup" || inProgress === "handleRedirect" || inProgress === "login") {

    return <AppLoader />;
  }



  return (
    <>
      {isPublicRoute(window.location.pathname) ? (
        <SettingsRoutes showSidebar={false} />
      ) : (
        <>
          {/* ✅ NEW: External user bypass — render AppRoutes directly without MSAL */}
          {localStorage.getItem("isExternalUser") === "true" ? (
            <AppRoutes
              isAuthenticated={true}       // ✅ Force authenticated
              userType={"ExternalUser"}    // ✅ Explicitly mark as external user
              activeAccount={{ idTokenClaims: {} }} // ✅ Dummy account object
              activeToken={true}           // ✅ Pretend token is valid
            />
          ) : (
            <>
              {/* 🔄 Unchanged: normal MSAL flow for internal users */}
              <AuthenticatedTemplate>
                {activeAccount && activeToken ? (
                  <AppRoutes
                    isAuthenticated={isAuthenticated}
                    userType={currentUserType}
                    activeAccount={activeAccount}
                    activeToken={activeToken}
                  />
                ) : null}
              </AuthenticatedTemplate>

              <UnauthenticatedTemplate>
                <SettingsRoutes showSidebar={false} />
              </UnauthenticatedTemplate>
            </>
          )}
        </>
      )}
    </>
  );

};

const customApp = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const uuid = urlParams.get("uuid");

  const dispatch = useDispatch();
  const isLocalStorage = useSelector((state) => state.auth.isLocalStorage);
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const currentUserType = useSelector((state) => state.auth.user);
  const [isAuthChecked, setIsAuthChecked] = useState(false);
  const [authCode, setAuthCode] = useState("");

  useEffect(() => {
    console.log("[CUSTOM APP] useEffect triggered, uuid:", uuid);

    const apiBase = process.env.REACT_APP_BASE_API_URL;
    const customAuthEndpoint = process.env.REACT_APP_CUSTOM_AUTH_API_ENDPOINT;
    const apiKey = process.env.REACT_APP_CUSTOM_AUTH_API_KEY;

    const fullAuthURL = `${apiBase}/${customAuthEndpoint}/corrigo/validate/${uuid}`;



    if (uuid) {


      dispatch(fetchInnferreToken(uuid))
        .then(async (token) => {

          try {
            const decodedToken = jwtDecode(token);


            if (decodedToken.exp && Date.now() / 1000 > decodedToken.exp) {
              console.warn("[CUSTOM APP] Token expired");
              setIsAuthChecked(true);
              return;
            }


            localStorage.setItem("token", token);
            localStorage.setItem("isExternalUser", "true");

            dispatch(
              loginUser({
                user: decodedToken.extension_Role || "ExternalUser",
                token: decodedToken,
              })
            );

            dispatch(fetchOrganizationDetails());
            // Wake up backend services after successful login
            wakeUpServices().catch(err => console.warn("Wake-up services failed:", err));
            setAuthCode(token);


            const datasetResponse = await dispatch(fetchExternalDataset(token));


            const datasets =
              datasetResponse?.payload ||
              datasetResponse?.data ||
              datasetResponse;



            if (Array.isArray(datasets) && datasets.length > 0) {
              const firstDataset = datasets[0];


              const { id, datasetName } = firstDataset;
              localStorage.setItem("isExternalUser", "true");


              window.location.replace(`/aiassistant?c=${id}&title=${encodeURIComponent(datasetName)}`);
              return;
            } else {

            }
          } catch (decodeError) {

          }

          setIsAuthChecked(true);
        })
        .catch((err) => {

          setIsAuthChecked(true);
        });
    } else {

      setIsAuthChecked(true);
    }
  }, [uuid, dispatch]);

  if (!isAuthChecked) {

    return <AppLoader />;
  }

  if (authCode) {

    try {
      const decodedToken = jwtDecode(authCode);
      if (decodedToken.exp && Date.now() / 1000 > decodedToken.exp) {

        return <App isAuthenticated={isAuthenticated} userType={currentUserType} isLocalStorage={isLocalStorage} />;
      }

      return (
        <AppRoutes
          isAuthenticated={true}
          userType="ExternalUser" //check
          activeAccount={{ idTokenClaims: decodedToken }}
          activeToken={true}
        />
      );
    } catch (e) {

      return <App isAuthenticated={isAuthenticated} userType={currentUserType} isLocalStorage={isLocalStorage} />;
    }
  }

  console.log("[CUSTOM APP] No authCode, rendering fallback <App>");
  return (
    <App
      isAuthenticated={isAuthenticated}
      userType={currentUserType}
      isLocalStorage={isLocalStorage}
    />
  );
};

export default customApp;
